import { useId, useState } from 'react';
import type { Saisie } from './champsFiche';
import {
  ecrireEtiquettes,
  ecrireRangs,
  lireEtiquettes,
  lireRangs,
} from './valeursFiche';

/**
 * Les contrôles de la fiche enfant, un par nature de réponse.
 *
 * Chacun ne connaît que deux choses : la chaîne stockée, et comment rendre la suivante.
 * Rien ici ne parle au serveur — la fiche rassemble les modifications et les enregistre
 * ensemble, pour que l'historique reçoive un lot nommé plutôt qu'une écriture par frappe.
 *
 * **La règle commune** : une valeur qui ne se relit pas dans la structure attendue n'est
 * jamais redécoupée. Le contrôle laisse alors la place à une ligne de texte portant la
 * valeur intacte (`SaisieTexte`), et le parent la reprend s'il le souhaite. Perdre la mise
 * en forme est un désagrément ; réécrire une posologie d'après une supposition ne l'est
 * pas.
 */
export function SaisieChamp({
  saisie,
  intitule,
  valeur,
  onChange,
}: {
  saisie: Saisie;
  /** Sert aux libellés accessibles des sous-champs, qui n'ont pas d'étiquette visible. */
  intitule: string;
  valeur: string;
  onChange: (valeur: string) => void;
}) {
  switch (saisie.type) {
    case 'pastilles':
      return (
        <SaisiePastilles
          options={saisie.options}
          libre={saisie.libre}
          intitule={intitule}
          valeur={valeur}
          onChange={onChange}
        />
      );
    case 'compteur':
      return (
        <SaisieCompteur
          min={saisie.min}
          max={saisie.max}
          defaut={saisie.defaut}
          intitule={intitule}
          valeur={valeur}
          onChange={onChange}
        />
      );
    case 'etiquettes':
      return (
        <SaisieEtiquettes
          exemple={saisie.exemple}
          intitule={intitule}
          valeur={valeur}
          onChange={onChange}
        />
      );
    case 'rangs':
      return (
        <SaisieRangs
          colonnes={saisie.colonnes}
          ajouter={saisie.ajouter}
          intitule={intitule}
          valeur={valeur}
          onChange={onChange}
        />
      );
    default:
      return <SaisieTexte intitule={intitule} valeur={valeur} onChange={onChange} />;
  }
}

function SaisieTexte({
  intitule,
  valeur,
  onChange,
}: {
  intitule: string;
  valeur: string;
  onChange: (valeur: string) => void;
}) {
  return (
    <input
      className="saisie-fiche"
      aria-label={intitule}
      value={valeur}
      onChange={(evenement) => onChange(evenement.target.value)}
    />
  );
}

/**
 * Un choix dans une liste courte, et une porte de sortie quand la liste ne suffit pas.
 *
 * La porte de sortie n'est pas une facilité : une taille de vêtements se lit aussi en
 * centimètres, et une liste qui refuserait « 128 cm » forcerait à choisir une valeur
 * fausse. Un groupe sanguin, lui, n'a pas de sortie — il n'existe que huit réponses.
 */
function SaisiePastilles({
  options,
  libre,
  intitule,
  valeur,
  onChange,
}: {
  options: readonly string[];
  libre: boolean;
  intitule: string;
  valeur: string;
  onChange: (valeur: string) => void;
}) {
  const horsListe = valeur !== '' && !options.includes(valeur);
  const [autreOuvert, setAutreOuvert] = useState(horsListe);

  return (
    <div className="saisie-pastilles">
      <div className="pastilles" role="group" aria-label={intitule}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={valeur === option ? 'pastille choisie' : 'pastille'}
            aria-pressed={valeur === option}
            onClick={() => {
              setAutreOuvert(false);
              onChange(valeur === option ? '' : option);
            }}
          >
            {option}
          </button>
        ))}
        {libre ? (
          <button
            type="button"
            className={horsListe ? 'pastille choisie' : 'pastille'}
            aria-pressed={horsListe}
            onClick={() => setAutreOuvert(true)}
          >
            Autre
          </button>
        ) : null}
      </div>

      {libre && (autreOuvert || horsListe) ? (
        <input
          className="saisie-fiche"
          aria-label={`${intitule} — autre valeur`}
          placeholder="128 cm"
          value={horsListe ? valeur : ''}
          onChange={(evenement) => onChange(evenement.target.value)}
        />
      ) : null}
    </div>
  );
}

/**
 * Un entier qu'on ajuste d'un cran.
 *
 * Tant que rien n'est renseigné, l'écran ne montre pas un nombre plausible : il le dit.
 * Un « 30 » affiché par défaut serait relu comme une pointure enregistrée, et personne ne
 * saurait qu'elle a été inventée par le formulaire.
 */
function SaisieCompteur({
  min,
  max,
  defaut,
  intitule,
  valeur,
  onChange,
}: {
  min: number;
  max: number;
  defaut: number;
  intitule: string;
  valeur: string;
  onChange: (valeur: string) => void;
}) {
  const nombre = Number.parseInt(valeur, 10);
  const renseigne = Number.isFinite(nombre);
  const borner = (n: number) => String(Math.min(max, Math.max(min, n)));

  if (!renseigne) {
    return (
      <div className="saisie-compteur">
        <span className="discret">Non renseignée</span>
        <button type="button" className="bouton secondaire" onClick={() => onChange(String(defaut))}>
          Renseigner
        </button>
      </div>
    );
  }

  return (
    <div className="saisie-compteur">
      <button
        type="button"
        className="cran"
        aria-label={`${intitule} — diminuer`}
        onClick={() => onChange(borner(nombre - 1))}
      >
        −
      </button>
      <output className="cran-valeur" aria-label={intitule}>
        {nombre}
      </output>
      <button
        type="button"
        className="cran"
        aria-label={`${intitule} — augmenter`}
        onClick={() => onChange(borner(nombre + 1))}
      >
        +
      </button>
      <button type="button" className="bouton lien" onClick={() => onChange('')}>
        Effacer
      </button>
    </div>
  );
}

/**
 * Une liste d'éléments courts.
 *
 * Une étiquette par allergène plutôt qu'un paragraphe : l'historique dit alors laquelle a
 * été ajoutée et quand, là où une phrase réécrite ne dit que « la phrase a changé ».
 */
function SaisieEtiquettes({
  exemple,
  intitule,
  valeur,
  onChange,
}: {
  exemple: string;
  intitule: string;
  valeur: string;
  onChange: (valeur: string) => void;
}) {
  const champ = useId();
  const [ajout, setAjout] = useState('');
  const etiquettes = lireEtiquettes(valeur);

  const ajouter = () => {
    const propre = ajout.trim();
    if (propre === '' || etiquettes.includes(propre)) return;
    onChange(ecrireEtiquettes([...etiquettes, propre]));
    setAjout('');
  };

  return (
    <div className="saisie-etiquettes">
      <ul className="etiquettes" aria-label={intitule}>
        {etiquettes.map((etiquette, rang) => (
          <li key={etiquette} className="etiquette">
            {etiquette}
            <button
              type="button"
              aria-label={`Retirer ${etiquette}`}
              onClick={() =>
                onChange(ecrireEtiquettes(etiquettes.filter((_, i) => i !== rang)))
              }
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div className="etiquette-ajout">
        <input
          id={champ}
          className="saisie-fiche"
          aria-label={`${intitule} — ajouter`}
          placeholder={exemple}
          value={ajout}
          onChange={(evenement) => setAjout(evenement.target.value)}
          // Entrée ajoute, et n'envoie pas le formulaire : on en saisit plusieurs d'affilée.
          onKeyDown={(evenement) => {
            if (evenement.key !== 'Enter') return;
            evenement.preventDefault();
            ajouter();
          }}
        />
        <button type="button" className="bouton secondaire" onClick={ajouter} disabled={ajout.trim() === ''}>
          Ajouter
        </button>
      </div>
    </div>
  );
}

/**
 * Une liste de lignes à plusieurs colonnes.
 *
 * **Les rangs vides vivent ici, et nulle part ailleurs.** `ecrireRangs` les efface, et
 * c'est voulu : une ligne ajoutée puis abandonnée ne doit pas s'enregistrer, ni dater un
 * champ pour rien dans un historique inaltérable. Mais un rang qu'on vient d'ajouter est
 * vide *par définition* — le relire depuis la chaîne le faisait disparaître dans le même
 * rendu, et les quatre boutons « Ajouter … » de la fiche paraissaient morts. Vider une
 * ligne du milieu la faisait de même s'évaporer sous les doigts.
 *
 * L'état à l'écran est donc tenu ici, et la chaîne n'en reçoit que ce qui a du contenu.
 * Ce que le dossier dit reprend la main dès que la valeur change ailleurs — enregistrement,
 * annulation, autre enfant : les rangs vides ne survivent pas, et c'est bien la règle.
 */
function SaisieRangs({
  colonnes,
  ajouter,
  intitule,
  valeur,
  onChange,
}: {
  colonnes: readonly string[];
  ajouter: string;
  intitule: string;
  valeur: string;
  onChange: (valeur: string) => void;
}) {
  const stockes = lireRangs(valeur, colonnes.length);
  const [rangs, setRangs] = useState<string[][]>(() => stockes ?? []);
  /** La chaîne dont l'état ci-dessus est le reflet, pour reconnaître un changement venu d'ailleurs. */
  const [refletDe, setRefletDe] = useState(valeur);

  let affiches = rangs;
  if (valeur !== refletDe) {
    setRefletDe(valeur);
    setRangs(stockes ?? []);
    // Ce rendu-ci continue avec la valeur neuve : lire `rangs` donnerait l'état d'avant.
    affiches = stockes ?? [];
  }

  // Valeur venue d'ailleurs : on la rend telle quelle plutôt que de la redécouper.
  if (stockes === null) {
    return (
      <div className="saisie-rangs">
        <SaisieTexte intitule={intitule} valeur={valeur} onChange={onChange} />
        <p className="aide">
          Cette valeur a été saisie en texte libre. Effacez-la pour la ressaisir en{' '}
          {colonnes.length} colonnes.
        </p>
      </div>
    );
  }

  const remplacer = (suivants: string[][]) => {
    setRangs(suivants);
    const ecrit = ecrireRangs(suivants);
    // Posé avant de prévenir le parent : ce qui revient est notre propre écriture, et la
    // relire comme un changement venu d'ailleurs effacerait les rangs encore vides.
    setRefletDe(ecrit);
    onChange(ecrit);
  };

  return (
    <div className="saisie-rangs">
      {affiches.map((rang, indexRang) => (
        // Les rangs n'ont pas d'identité propre — la valeur stockée est une chaîne, et
        // le rang n'est que sa position dedans. Retirer le deuxième remonte donc bien le
        // troisième, et React remonte le contenu avec lui.
        <div key={indexRang} className="rang-fiche" style={{ ['--colonnes' as string]: colonnes.length }}>
          {colonnes.map((colonne, indexColonne) => (
            <input
              key={colonne}
              className="saisie-fiche"
              aria-label={`${intitule} — ${colonne}`}
              placeholder={colonne}
              value={rang[indexColonne] ?? ''}
              onChange={(evenement) =>
                remplacer(
                  affiches.map((autre, i) =>
                    i === indexRang
                      ? autre.map((cellule, j) => (j === indexColonne ? evenement.target.value : cellule))
                      : autre,
                  ),
                )
              }
            />
          ))}
          <button
            type="button"
            className="retirer-rang"
            aria-label={`${intitule} — retirer la ligne ${indexRang + 1}`}
            onClick={() => remplacer(affiches.filter((_, i) => i !== indexRang))}
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        className="ajouter-rang"
        onClick={() => remplacer([...affiches, colonnes.map(() => '')])}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="currentColor" d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5Z" />
        </svg>
        {ajouter}
      </button>
    </div>
  );
}
